import React, { useRef, useEffect, useState } from 'react';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowUpDownIcon from '@mui/icons-material/ImportExport';
import SearchIcon from '@mui/icons-material/Search';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Badge, Box, Button, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import {
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridRenderCellParams,
  GridFilterItem,
} from '@mui/x-data-grid';

import ColumnOrderModal from '../ColumnOrderModal';
import FilterModal from '../FilterModal';

const COLUMN_ORDER_STORAGE_KEY = 'custom_data_grid_column_preferences';

interface ColumnPreferences {
  columnOrder: string[];
  hiddenColumns: string[];
  columnWidths: { [field: string]: number };
  lastUpdated?: number;
}
interface StoredPreferences {
  [pageId: string]: ColumnPreferences;
}

const loadColumnPreferences = (columns: GridColDef[], pageId: string): ColumnPreferences => {
  const stored = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
  const defaultOrder = columns.map((col) => col.field);
  const defaultWidths = Object.fromEntries(columns.map((col) => [col.field, col.width || 150]));

  if (!stored) {
    return {
      columnOrder: defaultOrder,
      hiddenColumns: [],
      columnWidths: defaultWidths,
    };
  }

  try {
    const preferences = JSON.parse(stored) as StoredPreferences;
    const pagePreferences = preferences?.[pageId];

    if (!pagePreferences?.columnOrder) {
      return {
        columnOrder: defaultOrder,
        hiddenColumns: [],
        columnWidths: defaultWidths,
      };
    }

    // Ensure all columns are present (in case new columns were added)
    const storedOrder = pagePreferences.columnOrder;
    const storedHidden = pagePreferences.hiddenColumns || [];
    const storedWidths = pagePreferences.columnWidths || {};
    const currentFields = new Set(columns.map((col) => col.field));
    const validStoredFields = storedOrder.filter((field) => currentFields.has(field));
    const validHiddenFields = storedHidden.filter((field) => currentFields.has(field));
    const missingFields = columns.map((col) => col.field).filter((field) => !storedOrder.includes(field));

    return {
      columnOrder: [...validStoredFields, ...missingFields],
      hiddenColumns: validHiddenFields,
      columnWidths: {
        ...defaultWidths,
        ...Object.fromEntries(Object.entries(storedWidths).filter(([field]) => currentFields.has(field))),
      },
      lastUpdated: pagePreferences.lastUpdated,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error loading column preferences:', error);
    return {
      columnOrder: defaultOrder,
      hiddenColumns: [],
      columnWidths: defaultWidths,
    };
  }
};

const setInitialColumns = (
  columns: GridColDef[],
  columnOrder: string[],
  hiddenColumns: string[],
  columnWidths: { [field: string]: number }
) => {
  return columnOrder
    .map((field) => {
      const col = columns.find((c) => c.field === field) as GridColDef;
      return { ...col, width: columnWidths[field] || col.width || 150 };
    })
    .filter((col) => !hiddenColumns.includes(col.field));
};

interface CustomDataGridProps<T> {
  rows: T[];
  columns: GridColDef[];
  filterModel: GridFilterModel;
  sortModel: GridSortModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  onSortModelChange: (model: GridSortModel) => void;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  rowCount: number;
  onRowClick?: (params: T, event: React.MouseEvent) => void;
  getRowClassName: (params: T) => string;
  topBar?: React.ReactNode;
  pageId: string;
  disableColumnOrder?: boolean;
  disableFilters?: boolean;
  disableSort?: boolean;
  disablePagination?: boolean;
  noResultsMessage?: string;
}

const CustomDataGrid = <T extends { id: number }>({
  rows,
  columns,
  filterModel,
  sortModel,
  onFilterModelChange,
  onSortModelChange,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  onRowClick,
  getRowClassName,
  topBar,
  pageId,
  disableColumnOrder = false,
  disableFilters = false,
  disableSort = false,
  disablePagination = false,
  noResultsMessage = 'No hay resultados',
}: CustomDataGridProps<T>) => {
  const tableBodyRef = useRef<HTMLTableSectionElement | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [isColumnOrderModalOpen, setColumnOrderModalOpen] = useState(false);
  const [selectedFilterField, setSelectedFilterField] = useState<string>('');
  const filteredColumns = columns.filter((col) => col.colSpan !== 0);
  // Load column order and visibility from localStorage or use default
  const { columnOrder, hiddenColumns, columnWidths } = loadColumnPreferences(filteredColumns, pageId);
  const [updatedColumns, setUpdatedColumns] = useState<GridColDef[]>(
    setInitialColumns(filteredColumns, columnOrder, hiddenColumns, columnWidths)
  );

  useEffect(() => {
    // If the columns change, update the updatedColumns
    setUpdatedColumns(setInitialColumns(filteredColumns, columnOrder, hiddenColumns, columnWidths));
  }, [columns]);

  const saveColumnPreferences = (preferences: { columnOrder: string[]; hiddenColumns: string[] }) => {
    try {
      const stored = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
      const storedPreferences = (stored ? JSON.parse(stored) : {}) as StoredPreferences;

      // Save current column widths
      const currentWidths = Object.fromEntries(
        updatedColumns.map((col) => [col.field, col.width || columnWidths[col.field] || 150])
      );

      storedPreferences[pageId] = {
        columnOrder: preferences.columnOrder,
        hiddenColumns: preferences.hiddenColumns,
        columnWidths: currentWidths,
        lastUpdated: Date.now(),
      };

      localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(storedPreferences));

      // Update the columns in the grid
      setUpdatedColumns(
        preferences.columnOrder
          .map((field) => {
            const col = filteredColumns.find((c) => c.field === field) as GridColDef;
            return { ...col, width: currentWidths[field] || col.width || 150 };
          })
          .filter((col) => !preferences.hiddenColumns.includes(col.field))
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving column preferences:', error);
    }
  };

  const handleSort = (field: string) => {
    const existingSort = sortModel.find((sort) => sort.field === field);
    // change from asc to desc or remove the sort
    let newSortModel = existingSort
      ? sortModel.map((sort) =>
          sort.field === field ? { ...sort, sort: sort.sort === 'asc' ? ('desc' as const) : undefined } : sort
        )
      : [...sortModel, { field, sort: 'asc' as const }];
    newSortModel = newSortModel.filter((sort) => sort.sort !== undefined);
    onSortModelChange(newSortModel);
  };

  const handleFilterChange = (items: GridFilterItem[]) => {
    const newFilterModel = {
      items,
    };
    onFilterModelChange(newFilterModel);
  };

  const openFilterModalWithField = (field: string) => {
    setSelectedFilterField(field);
    setFilterModalOpen(true);
  };

  const getRowValue = (row: T, column: GridColDef) => {
    let value = row[column.field as keyof T] as string;
    if (column.valueGetter) {
      value = column.valueGetter(value as never, row, column, {} as never) as string;
    }
    if (column.valueFormatter && value !== undefined && value !== null) {
      value = column.valueFormatter(value as never, row, column, {} as never) as string;
    }
    if (column.renderCell) {
      return column.renderCell({
        row,
        field: column.field,
        value,
        api: {},
      } as GridRenderCellParams);
    }
    return value || '';
  };

  const getSortIcon = (field: string) => {
    if (disableSort) {
      return null;
    }
    const sort = sortModel.find((s) => s.field === field);
    if (sort) {
      return (
        <Box>
          {sort.sort === 'asc' ? (
            <ArrowUpwardIcon sx={{ mr: 1, cursor: 'pointer' }} onClick={() => handleSort(field)} />
          ) : (
            <ArrowDownwardIcon sx={{ mr: 1, cursor: 'pointer' }} onClick={() => handleSort(field)} />
          )}
        </Box>
      );
    }
    if (columns.find((c) => c.field === field)?.sortable === false) {
      return null;
    }
    return (
      <Box className="sort-on-hover" sx={{ display: 'none' }}>
        <ArrowUpDownIcon
          fontSize="inherit"
          sx={{ mr: 1, cursor: 'pointer', color: 'grey' }}
          onClick={() => handleSort(field)}
        />
      </Box>
    );
  };

  const hasFilter = (field: string) => {
    return filterModel.items.some((item) => item.field === field);
  };

  const getFilterIcon = (field: string) => {
    if (disableFilters || columns.find((c) => c.field === field)?.filterable === false) {
      return null;
    }
    if (hasFilter(field)) {
      return (
        <SearchIcon
          sx={{ mr: 1, cursor: 'pointer', color: 'primary.main' }}
          onClick={() => openFilterModalWithField(field)}
        />
      );
    }
    return (
      <Box className="sort-on-hover" sx={{ display: 'none' }}>
        <Tooltip title="Buscar en esta columna">
          <SearchIcon
            fontSize="inherit"
            sx={{ mr: 1, cursor: 'pointer', color: 'grey' }}
            onClick={(e) => {
              e.stopPropagation();
              openFilterModalWithField(field);
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    onPaginationModelChange(model);
  };

  const handleResize = (field: string, newWidth: number) => {
    setUpdatedColumns((cols) => cols.map((col) => (col.field === field ? { ...col, width: newWidth } : col)));

    // Save the new width to localStorage
    try {
      const stored = localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
      const storedPreferences = (stored ? JSON.parse(stored) : {}) as StoredPreferences;
      const currentPreferences = storedPreferences[pageId] || {
        columnOrder,
        hiddenColumns,
        columnWidths: {},
        lastUpdated: Date.now(),
      };

      const newWidths = {
        ...currentPreferences.columnWidths,
        [field]: newWidth,
      };

      storedPreferences[pageId] = {
        ...currentPreferences,
        columnWidths: newWidths,
        lastUpdated: Date.now(),
      };

      localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(storedPreferences));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving column width:', error);
    }
  };

  const handleMouseDown = (e: React.MouseEvent, field: string) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = updatedColumns.find((col) => col.field === field)?.width || 150;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.max(50, startWidth + moveEvent.clientX - startX);
      handleResize(field, newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  useEffect(() => {
    if (tableBodyRef.current) {
      const selectedRow = tableBodyRef.current.querySelector('.previous');
      if (selectedRow) {
        selectedRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [rows, getRowClassName]);

  const sortTooltipTitle =
    sortModel.length > 0
      ? `Ordenado por: ${sortModel.map((sort) => `${sort.field} ${sort.sort === 'asc' ? '↑' : '↓'}`).join(', ')}`
      : 'Ordenamiento';

  const getRowBackgroundColor = (row: T) => {
    const style: { backgroundColor?: string; opacity?: number; borderLeft?: string } = {};
    if (hoveredRow === row.id) {
      style.opacity = 0.5;
    }
    const className = getRowClassName(row);
    if (className.includes('previous')) {
      style.backgroundColor = '#e0e0e0';
      style.borderLeft = '5px solid #000';
    }
    if (className.includes('highlighted-')) {
      style.backgroundColor = `rgba(2, 137, 209, 0.${className.slice(-1)})`;
    }
    return style;
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        {!disableColumnOrder && (
          <Tooltip title="Configurar columnas">
            <SwapHorizIcon sx={{ cursor: 'pointer' }} onClick={() => setColumnOrderModalOpen(true)} color="disabled" />
          </Tooltip>
        )}
        {!disableSort && (
          <Tooltip title={sortTooltipTitle}>
            <Badge badgeContent={sortModel.length} color="primary" invisible={sortModel.length === 0}>
              <ArrowUpDownIcon color={sortModel.length > 0 ? 'primary' : 'disabled'} />
            </Badge>
          </Tooltip>
        )}
        {!disableFilters && (
          <Tooltip title="Filtros">
            <Badge badgeContent={filterModel.items.length} color="primary" invisible={filterModel.items.length === 0}>
              <SearchIcon
                sx={{ cursor: 'pointer' }}
                onClick={() => setFilterModalOpen(true)}
                color={filterModel.items.length > 0 ? 'primary' : 'disabled'}
              />
            </Badge>
          </Tooltip>
        )}
        {topBar}
      </Box>
      <FilterModal
        open={isFilterModalOpen}
        onClose={() => {
          setFilterModalOpen(false);
          setSelectedFilterField('');
        }}
        onFilterChange={handleFilterChange}
        columns={columns}
        filterModel={{
          items:
            selectedFilterField && !filterModel.items.find((item) => item.field === selectedFilterField)
              ? [...filterModel.items, { field: selectedFilterField, operator: '', value: '' }]
              : filterModel.items,
        }}
      />
      {isColumnOrderModalOpen && (
        <ColumnOrderModal
          open={isColumnOrderModalOpen}
          onClose={() => setColumnOrderModalOpen(false)}
          columns={filteredColumns}
          columnOrder={columnOrder}
          hiddenColumns={hiddenColumns}
          onSavePreferences={saveColumnPreferences}
        />
      )}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflowX: 'auto', position: 'relative' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
              <tr>
                {updatedColumns.map((column) => (
                  <th
                    key={column.field}
                    style={{
                      width: column.width,
                      padding: '8px',
                      textAlign: 'left',
                      fontWeight: 'bold',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      borderBottom: '1px solid #ddd',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover .sort-on-hover': {
                          display: 'block',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          width: 'calc(100% - 15px)',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        {column.headerName || column.field}
                      </Typography>
                      {getSortIcon(column.field)}
                      {getFilterIcon(column.field)}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: disableColumnOrder ? 'default' : 'col-resize',
                          width: 15,
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          bottom: 0,
                          zIndex: 1,
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                        }}
                        onMouseDown={(e) => !disableColumnOrder && handleMouseDown(e, column.field)}
                      >
                        <Box
                          sx={{
                            width: '2px',
                            height: '80%',
                            border: '1px solid lightgray',
                            backgroundColor: 'lightgray',
                          }}
                          onMouseDown={(e) => !disableColumnOrder && handleMouseDown(e, column.field)}
                        />
                      </Box>
                    </Box>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody ref={tableBodyRef} className="MuiDataGrid-virtualScroller" style={{ overflowY: 'auto' }}>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    cursor: 'pointer',
                    ...getRowBackgroundColor(row),
                  }}
                  className={getRowClassName(row)}
                  onClick={(event) => onRowClick && onRowClick(row, event)}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  {updatedColumns.map((column) => (
                    <td
                      key={column.field}
                      style={{
                        padding: '8px',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        borderBottom: '1px solid #ddd',
                      }}
                    >
                      {getRowValue(row, column)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <Box m={2}>
              <Typography>{noResultsMessage}</Typography>
            </Box>
          )}
        </Box>
      </Box>
      {!disablePagination && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            pt: 1,
            borderTop: '1px solid #ddd',
          }}
        >
          <Select
            value={paginationModel.pageSize}
            onChange={(event) => handlePaginationModelChange({ page: 0, pageSize: Number(event.target.value) })}
            size="small"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
          <Typography>
            {paginationModel.page * paginationModel.pageSize + 1} -{' '}
            {Math.min(paginationModel.page * paginationModel.pageSize + paginationModel.pageSize, rowCount)} de{' '}
            {rowCount}
          </Typography>
          <Button
            disabled={paginationModel.page === 0}
            onClick={() =>
              handlePaginationModelChange({ page: paginationModel.page - 1, pageSize: paginationModel.pageSize })
            }
          >
            Anterior
          </Button>
          <Button
            disabled={paginationModel.page >= Math.ceil(rowCount / paginationModel.pageSize) - 1}
            onClick={() =>
              handlePaginationModelChange({ page: paginationModel.page + 1, pageSize: paginationModel.pageSize })
            }
          >
            Siguiente
          </Button>
        </Box>
      )}
    </Box>
  );
};

CustomDataGrid.defaultProps = {
  onRowClick: null,
  topBar: null,
  disableColumnOrder: false,
  disableFilters: false,
  disableSort: false,
  disablePagination: false,
  noResultsMessage: 'No hay resultados',
};

export default CustomDataGrid;
